import React, { useEffect, useState } from 'react'
import { Button, Dialog, DialogTitle, Grid, Typography } from '@mui/material'
import axios from 'axios'
import ReactGA from 'react-ga4'
import { useNavigate } from 'react-router-dom'
import Footer from 'components/Footer'
import Header from 'components/Header'
import TextField from 'components/shared/textField'
import useQueryParams from 'hooks/useQueryParams'
import {
  SetAvailablePlansSignUp,
  SetIsMoreThanOneStudioSignUp,
  SetSelectedStudioSignUp,
  SetStudiosSignUp,
} from 'redux/SignUp/persist'
import { useAppDispatch, useAppSelector } from 'redux/store'
import { formatPhoneNumber } from 'utils/formatPhoneNumber'
import { createNavigateWithQueryParams } from 'utils/navigateWithQueryParams'
import styles from './Otp.module.scss'
import { AppConstants, LeadStage } from '../../../AppConstants/AppConstants'

const OtpSignUp: React.FC = () => {
  const { getAllParams, getParams, hasCampaign, hasContent } = useQueryParams()
  const id = getParams('utm_campaign')
  const utm_source = getParams('utm_source')
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  // Local state
  const [code, setCode] = useState('')
  const [otpError, setOtpError] = useState(false)
  const [disableVerifyBtn, setDisableVerifyBtn] = useState(true)
  const [errorType, setErrorType] = useState('')
  const [modal, setModal] = useState(false)

  // Signup flow state management
  const firstname = useAppSelector((state) => state.signUpPersist.firstname)
  const phoneNumber = useAppSelector((state) => state.signUpPersist.phoneNumber)
  const formattedPhoneNumber = useAppSelector((state) => state.signUpPersist.formattedPhoneNumber)
  const isMoreThanOneStudio = useAppSelector((state) => state.signUpPersist.isMoreThanOneStudioSignUp)
  const trackerCount = useAppSelector((state) => state.signUpPersist.trackerCountSignUp)
  const userId = useAppSelector((state) => state.signUpPersist.userId)

  const [resendTimer, setResendTimer] = useState(60) // 1 minute

  useEffect(() => {
    if (resendTimer === 0) {
      setTimeout(() => {
        setResendTimer(60)
      }, 60000)
    }
  }, [resendTimer])
  // Create reusable navigation function
  const navigateWithQueryParams = createNavigateWithQueryParams(navigate, getAllParams)

  const onVerify = async () => {
    setDisableVerifyBtn(true)
    if (code === '') {
      setOtpError(true)
      setErrorType('Empty')
    } else {
      try {
        const email = localStorage.getItem(AppConstants.USER_EMAIL)
        const response: any = await axios
          .post(AppConstants.API_URL + '/referral/lead/register/verify-otp', {
            phone: '+' + phoneNumber.replace(/-/g, ''),
            code: code,
            signupThrough: 'web',
            firstname: firstname,
            email: email,
            id: userId,
            referralCode: hasCampaign ? id : null,
          })
          .then((res) => res)
          .catch((err) => err.response)

        if (response && response.data && response.data.code === 'success') {
          localStorage.setItem(AppConstants.ACCESS_TOKEN, response?.data?.data?.accessToken)
          let localToken = response.data?.data?.accessToken
          let tokenParts = localToken.split('.')
          let tokenPayload = tokenParts[1]
          let tokenPayloadStr = atob(tokenPayload)
          let decodedToken: any = JSON.parse(tokenPayloadStr)
          let tempCurrentRole: any = ''
          if (decodedToken) {
            if (decodedToken.roles && decodedToken.roles.length === 1) {
              tempCurrentRole = decodedToken.roles[0]
            }
          } else {
            tempCurrentRole = null
          }
          localStorage.setItem(AppConstants.CURRENTROLE_TOKEN, tempCurrentRole)
          window.oaiq?.('measure', 'lead_created', { type: 'customer_action' })

          if (utm_source === 'facebook' || utm_source === 'meta') {
            //@ts-ignore
            // fbq('track', 'SubmitApplication')
            //@ts-ignore
            // fbq('track', 'Lead')
            await new Promise((resolve) => setTimeout(resolve, 300))
          } else {
            ReactGA.event('conversion', {
              send_to: process.env.REACT_APP_GA_CONVERSION_ID || 'AW-11337579902/m3hPCJKL4LgZEP76lp4q',
            })
          }
          await postLeadStage()

          const isOboardingCompleted = await isOboardingComplete()
          if (isOboardingCompleted) {
            // navigate('/signup/download')
            setModal(true)
            return
          }

          // Navigate to next step in signup flow based on parameters
          if (hasCampaign) {
            if (getAllParams()['loc']) {
              getPlans()
              getAllStudios()
            } else {
              getPlans()
              navigateWithQueryParams('/signup/location')
            }
          } else {
            navigateWithQueryParams('/signup/location')
          }
        } else if (response && response.data && response.data.code === 'user_already_exists') {
          setModal(true)
          setDisableVerifyBtn(false)
        } else if (response && response.data && response.data.code === 'invalid_value') {
          setOtpError(true)
          setErrorType('Invalid')
          setDisableVerifyBtn(false)
        } else if (response && response.data && response.data.code === 'code_expired') {
          setOtpError(true)
          setErrorType('Code_Expired')
          setDisableVerifyBtn(false)
        } else if (response && response.data && response.data.code === 'user_not_found') {
          setErrorType('Not_User')
          setDisableVerifyBtn(false)
        } else if (response && response.data && response.data.statusCode >= 400 && response.data.statusCode <= 599) {
          setErrorType('Error_Occurred')
          setDisableVerifyBtn(false)
        }
      } catch (error) {
        console.error('An error occurred:', error)
        setErrorType('Error_Occurred')
        setDisableVerifyBtn(false)
      }
    }
  }

  useEffect(() => {
    setTimeout(() => {
      if (resendTimer > 0) {
        setResendTimer(resendTimer - 1)
      }
    }, 1000)
  }, [resendTimer])

  const resendOtp = async () => {
    try {
      // Extract query parameters for payload
      const email = localStorage.getItem(AppConstants.USER_EMAIL) || null
      const utm_source = getParams('utm_source') || null
      const utm_medium = getParams('utm_medium') || null
      const utm_campaign = getParams('utm_campaign') || null
      const market = getParams('market') || null
      const loc = getParams('loc') || null

      const response: any = await axios
        .post(AppConstants.API_URL + '/referral/lead/register/signup', {
          firstname: firstname,
          phone: '+' + phoneNumber.replace(/-/g, ''),
          email: email,
          utm_source: utm_source,
          utm_medium: utm_medium,
          utm_campaign: utm_campaign,
          market: market,
          loc: loc,
        })
        .then((res) => res)
        .catch((err) => err.response)

      if (
        response &&
        response?.data?.code === 'success' &&
        response?.data?.message === 'Verification code sent successfully'
      ) {
        setErrorType('')
        setOtpError(false)
      }
    } catch (error) {
      console.error('Error resending OTP:', error)
    } finally {
      setResendTimer(60)
    }
  }

  async function getPlans() {
    try {
      const response: any = await axios.get(`${AppConstants.API_URL}/payment-plan/v1?campaign=${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('userToken')}`,
          'x-platform-id': process.env.REACT_APP_PLATFORM_ID || '',
        },
      })
      if (response && response?.data && response?.data?.data) {
        dispatch(SetAvailablePlansSignUp(response?.data?.data))
      } else if (response?.data?.statusCode === 401) {
        console.log('error')
      }
    } catch (error) {
      console.error(error)
    }
  }

  async function isOboardingComplete() {
    try {
      const response: any = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/auth/me`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('userToken')}`,
          'x-platform-id': process.env.REACT_APP_PLATFORM_ID || '',
        },
      })
      return response && response?.data.code === 'success' && response?.data?.data?.previousVisits
    } catch (error) {
      console.error(error)
    }
  }

  const getAllStudios = async () => {
    const response: any = await axios
      .get(AppConstants.API_URL + '/clinic', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('userToken')}`,
          'x-platform-id': process.env.REACT_APP_PLATFORM_ID || '',
        },
      })
      .then((res) => {
        return res
      })
      .catch((err) => {
        return err.response
      })

    if (response?.data?.code === 'success') {
      dispatch(SetStudiosSignUp(response?.data?.data))

      // Get 'loc' parameter from query params
      const loc = getAllParams()['loc']
      const studios = response?.data?.data
      if (studios) {
        if (studios.length > 1) {
          dispatch(SetIsMoreThanOneStudioSignUp(true))
        }
      }

      if (Array.isArray(studios) && loc) {
        // Search for a studio with a matching slug (case-insensitive)
        const matchedStudio = studios.find((studio: any) => studio?.slug?.toLowerCase() === loc.toLowerCase())
        if (matchedStudio) {
          dispatch(SetSelectedStudioSignUp(matchedStudio))
          //navigateWithQueryParams('/signup/slot')
          navigateWithQueryParams('/signup/location')
        } else {
          navigateWithQueryParams('/signup/location')
        }
      }
    } else {
      return false
    }
  }

  const postLeadStage = async () => {
    await axios
      .post(
        AppConstants.API_URL + '/lead/stage/update',
        { stage: LeadStage.otp },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('userToken')}`,
            'x-platform-id': process.env.REACT_APP_PLATFORM_ID || '',
          },
        },
      )
      .then((res) => {
        return res
      })
      .catch((err) => {
        return err?.response
      })
  }

  const handleKeyDown = (e: any) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (!disableVerifyBtn) {
        onVerify()
      }
    }
  }

  useEffect(() => {
    if (utm_source === 'google') {
      ReactGA.event('page_view', {
        page_title: 'SignUp|Otp',
        page_location: `/signup/otp`,
      })
    }
  }, [utm_source])

  useEffect(() => {
    if (code.length >= 6) {
      setDisableVerifyBtn(false)
    } else {
      setDisableVerifyBtn(true)
    }
  }, [code])

  const styless: any = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: 260,
    height: 170,
    overflow: 'hidden',
    backgroundColor: '#050624',
    color: 'white',
    borderRadius: '8px',
    p: 4,
  }

  return (
    <div className={styles.container}>
      <Header
        headerText=""
        showBackButton={false}
        onBack={() => {
          setCode('')
          setErrorType('')
          setOtpError(false)
          navigateWithQueryParams('/signup/phone')
        }}
        hideIcon={false}
      />
      <div className={styles.verifyOtpContainer}>
        <p className={styles.codeText}>
          Please enter the 6 digit code sent to:&nbsp;
          <b className={styles.boldText}>{formatPhoneNumber(formattedPhoneNumber)}</b>
        </p>
        <form
          onSubmit={(e) => {
            e.preventDefault()
          }}
        >
          <Grid container spacing={2} sx={{ marginBottom: '12px', marginTop: '30px' }}>
            <Grid item xs={12}>
              <TextField
                autoFocus
                inputProps={{
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                  maxLength: 6,
                }}
                sx={
                  otpError
                    ? {
                        backgroundImage: "url('images/error.svg')",
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 15px center',
                        textTransform: 'capitalize',
                      }
                    : {}
                }
                value={code}
                placeholder="Enter Code"
                label="Verification Code"
                name="code"
                error={otpError}
                onChange={(e) => {
                  setCode(e.target.value)
                  setOtpError(false)
                  if (e.target.value.length >= 6) {
                    setDisableVerifyBtn(false)
                  } else {
                    setDisableVerifyBtn(true)
                  }
                }}
                onKeyDown={handleKeyDown}
              />
            </Grid>
          </Grid>
        </form>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px' }}>
          <div
            className={resendTimer === 0 ? styles.signUpText : styles.resendOtpText}
            onClick={() => {
              if (resendTimer === 0) {
                resendOtp()
              }
            }}
          >
            Resend OTP
            {resendTimer > 0
              ? ` in ${Math.floor(resendTimer / 60)}:${(resendTimer % 60).toString().padStart(2, '0')}`
              : ''}
            {}
          </div>
          <div
            className={resendTimer === 0 ? styles.signUpText : styles.resendOtpText}
            onClick={() => {
              if (resendTimer === 0) {
                setCode('')
                setDisableVerifyBtn(true)
                if (otpError || errorType !== '') {
                  setErrorType('')
                  setOtpError(false)
                }
                navigateWithQueryParams('/signup/phone')
              }
            }}
          >
            Wrong Number?
          </div>
        </div>

        <Grid item xs={12}>
          {otpError && (
            <div className={styles.errorContainer}>
              <Typography sx={{ fontSize: '14px', fontWeight: 700 }}>
                {errorType === 'Empty' && `Please enter passcode`}
                {errorType === 'userExists' && `User already exists`}
                {errorType === 'Invalid' && `Invalid Code`}
                {errorType === 'Code_Expired' && `Oops! code expired, Click Resend`}
                {errorType === 'Error_Occurred' && `Oops! something went wrong`}
              </Typography>
              <div
                style={{
                  display: 'flex',
                  flex: '1',
                  flexDirection: 'row',
                  justifyContent: 'flex-end',
                  paddingRight: '12px',
                }}
              >
                <img
                  onClick={() => {
                    setOtpError(false)
                  }}
                  style={{ cursor: 'pointer' }}
                  src={'/images/wrong.svg'}
                ></img>
              </div>
            </div>
          )}
        </Grid>
        {modal && (
          <Dialog
            onClose={() => {}}
            open={modal}
            PaperProps={{
              style: {
                background: 'var(--color-background)',
                borderRadius: '12px',
                padding: '10px 12px',
                minWidth: 300,
                maxWidth: 300,
                minHeight: 'auto',
                // height: 170,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                flex: 1, // align everything to the left
                textAlign: 'left', // global text alignment
              },
            }}
          >
            <DialogTitle
              style={{
                fontSize: '16px',
                fontWeight: 700,
                color: 'var(--color-text-primary)',
                textAlign: 'left',
                marginBottom: '12px',
                padding: 0,
                width: '100%',
              }}
            >
              Account Already Exists
            </DialogTitle>
            <Typography
              style={{
                color: 'var(--color-text-primary)',
                fontSize: '14px',
                textAlign: 'left',
                marginBottom: '24px',
                lineHeight: 1.5,
                width: '100%',
              }}
            >
              Looks like you already have an account! Download the KIRO app to sign in and pick up where you left off.
            </Typography>
            <Button
              color="primary"
              style={{
                marginBottom: '0.3rem',
                height: '42px',
                padding: '0px 8px 0px 8px',
                fontSize: '14px',
                alignSelf: 'center',
              }}
              onClick={() => {
                navigateWithQueryParams('/signup/download')
              }}
            >
              Ok
            </Button>
          </Dialog>
        )}
      </div>
      <Footer
        buttonText={'Verify'}
        isLoading={false}
        trackerIndex={3}
        shouldShowFour={true}
        shouldShowFive={true}
        shouldShowSix={true}
        shouldShowSeven={trackerCount >= 7}
        shouldShowEight={trackerCount >= 8}
        isDisabled={disableVerifyBtn}
        buttonId={'verify-btn'}
        buttonType={'submit'}
        onButtonClick={() => {
          disableVerifyBtn ? undefined : onVerify()
        }}
      ></Footer>
    </div>
  )
}

export default OtpSignUp
